import * as React from "react";
import { Captions, FileEdit, Hash, Settings, WandSparkles } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";

import { Badge, Button } from "@/components/ui";
import { HashTagSet } from "../HashTagSet";
import { EditorCaptionSettings, EditorVideoProjectField } from "../EditorCaptionSettings";
import { EditorPostingPreferences } from "../EditorPostingPreferences";
import { VideoProject } from "@/services/bedrock";
import { cn } from "@/lib/utils";

interface Props {
  videoProject: VideoProject;
  disabled: boolean;
  hasCaptionCoach: boolean;
  showHashtagToggle: boolean;
  postableSocialNetworks: string[];
  finalMessage: string;
  hashtags: string;
  initialHashtags: string;
  setHashtags: React.Dispatch<React.SetStateAction<string>>;
  onChangeField: (field: EditorVideoProjectField, value: any) => void;
  setShowAIModal: (show: boolean) => void;
  blockedWordsInCaption?: string;
}

export const EditorPostInfo: React.FC<Props> = ({
  videoProject,
  disabled,
  hasCaptionCoach,
  showHashtagToggle,
  postableSocialNetworks,
  finalMessage,
  hashtags,
  initialHashtags,
  setHashtags,
  onChangeField,
  setShowAIModal,
  blockedWordsInCaption,
}) => {
  const [hashtagsListError, setHashtagsListError] = React.useState<string[] | undefined>();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <FileEdit className="size-4.5 text-indigo-600" />
          Post Info
        </label>
      </div>
      <div className="videoProjectContainer flex flex-col gap-4 bg-slate-50/50 p-5 rounded-xl border border-slate-200">
        <div className="flex flex-col gap-2">
          <div className="flex flex-row justify-between items-center w-full">
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <Captions className="size-3.5 text-slate-400" />
              Caption
            </label>
            {!disabled && hasCaptionCoach && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAIModal(true)}
                className="text-slate-600 flex items-center gap-1 text-xs"
              >
                <WandSparkles className="size-3.5" />
                Generate Caption
              </Button>
            )}
          </div>
          {blockedWordsInCaption && blockedWordsInCaption.length > 0 && (
            <div className="bg-rose-50 border border-rose-100 p-3 rounded-lg text-rose-600 text-xs font-semibold">
              {`Remove or rephrase the following words: ${blockedWordsInCaption}`}
            </div>
          )}
          {disabled ? (
            <div className="p-3 border rounded-lg bg-white border-slate-200 text-slate-800 text-sm whitespace-pre-wrap leading-relaxed select-text shadow-2xs">
              {finalMessage}
            </div>
          ) : (
            <TextareaAutosize
              id="message"
              name="message"
              minRows={3}
              className={cn(
                "flex w-full rounded-lg border border-input bg-white px-3 py-2 text-base placeholder:text-muted-foreground outline-none transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none"
              )}
              disabled={disabled}
              value={videoProject.post?.message || ""}
              onChange={e => {
                onChangeField("message", e.target.value);
              }}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 pt-3 border-t border-slate-100">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <Hash className="size-3.5 text-slate-400" />
            Hashtags
          </label>
          {disabled ? (
            hashtags.trim() ? (
              <div className="flex flex-wrap gap-2">
                {hashtags
                  .trim()
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((tag, idx) => (
                    <Badge key={`${tag}-${idx}`} variant="outline" className="text-sm">
                      {tag.startsWith("#") ? tag : `#${tag}`}
                    </Badge>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No hashtags</p>
            )
          ) : (
            <>
              {hashtagsListError && hashtagsListError.length > 0 && (
                <div className="flex flex-col gap-1">
                  {hashtagsListError.map((error, idx) => (
                    <div key={idx} className="text-xs text-rose-500 font-semibold">
                      • {error}
                    </div>
                  ))}
                </div>
              )}
              <HashTagSet
                title="Hashtags..."
                coachId={videoProject.coachId}
                onError={setHashtagsListError}
                customTagStyle="background: rgba(61, 174, 245, 0.2);"
                defaultHashtagList={initialHashtags}
                onSuccess={newTags => {
                  const hashtagsString = newTags.reduce(
                    (accumulator, hashtag) => accumulator + "#" + hashtag.displayValue + " ",
                    ""
                  );
                  if (newTags.length <= 30 && hashtagsString.trim() !== hashtags.trim()) {
                    setHashtags(hashtagsString);
                  }
                }}
              />
            </>
          )}
        </div>

        <div className="flex flex-col gap-2 pt-4 border-t border-slate-100">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <Settings className="size-3.5 text-slate-400" />
            Caption Preferences
          </label>
          <EditorCaptionSettings
            videoProject={videoProject}
            disabled={disabled}
            showHashtagToggle={showHashtagToggle}
            onChangeField={onChangeField}
          />
        </div>

        <div className="flex flex-col gap-3 pt-4 border-t border-slate-100">
          <EditorPostingPreferences
            videoProject={videoProject}
            disabled={disabled}
            postableSocialNetworks={postableSocialNetworks}
            onChangeField={onChangeField}
          />
        </div>
      </div>
    </div>
  );
};
